import React from 'react';
import { SENTIMENT_META, sentimentTone } from '../../lib/format';

/**
 * Status badges.
 *
 * THE RULE THIS FILE ENFORCES
 * ---------------------------
 * Every badge pairs an icon with a word. Tone is a second channel, never the
 * only one: a red "Missed" and a grey "No answer" must still be distinguishable
 * with the colour stripped out, on a monochrome print, and to the ~8% of male
 * users with a red/green deficiency.
 *
 * The other rule is that these components never guess. A call whose `answered`
 * flag never arrived is "Unknown", not "Answered" — the old code read
 * `answered ? 'Answered' : 'Missed'`, which quietly reported every call still
 * being ingested as a missed one and inflated the miss count on every screen it
 * appeared on.
 */

const TONES = {
  good: 'text-good-text bg-good-tint border-good-line',
  warn: 'text-warn-text bg-warn-tint border-warn-line',
  bad: 'text-bad-text bg-bad-tint border-bad-line',
  brand: 'text-brand bg-brand-tint border-brand-line',
  neutral: 'text-ink-700 bg-surface-2 border-line',
};

// 4px radius is the badge/chip step. `whitespace-nowrap` because a two-word
// label wrapping inside a 20px pill is worse than the cell scrolling.
const SHELL =
  'inline-flex items-center align-middle whitespace-nowrap rounded-sm border font-medium';

const SIZES = {
  sm: 'h-5 gap-1 px-1.5 text-micro',
  md: 'h-6 gap-1.5 px-2 text-label',
};

const shellClass = (size) => `${SHELL} ${SIZES[size] || SIZES.md}`;

/**
 * Render the `icon` prop. A string is treated as a Font Awesome glyph name
 * ('fa-check'); anything else is passed through, so a caller can hand in an
 * inline SVG without this file needing to know about it.
 */
const renderIcon = (icon) => {
  if (!icon) return null;
  if (typeof icon === 'string') {
    return <i className={`fas ${icon} shrink-0`} aria-hidden="true" />;
  }
  return icon;
};

/**
 * A value that may be a real boolean, the string form the filter layer speaks
 * ('true' / 'false'), or absent. Returns `null` for anything it cannot read as
 * a boolean, which the callers below render as "Unknown".
 */
const asBool = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return Number.isFinite(v) ? v !== 0 : null;
  const s = String(v).trim().toLowerCase();
  if (s === 'true' || s === 't' || s === '1' || s === 'yes' || s === 'y') return true;
  if (s === 'false' || s === 'f' || s === '0' || s === 'no' || s === 'n') return false;
  return null;
};

/**
 * @param {object} props
 * @param {'good'|'warn'|'bad'|'brand'|'neutral'} [props.tone]
 * @param {'sm'|'md'} [props.size]
 * @param {string|React.ReactNode} [props.icon] Font Awesome name or a node.
 */
export function Badge({ tone = 'neutral', size = 'md', icon, children, className = '', ...rest }) {
  return (
    <span className={`${shellClass(size)} ${TONES[tone] || TONES.neutral} ${className}`} {...rest}>
      {renderIcon(icon)}
      {children}
    </span>
  );
}

/**
 * Sentiment as judged by the analyser.
 *
 * Neutral is GREY, not amber — a neutral call is not a warning, and amber is
 * reserved app-wide for SLA breach, quota, and trial expiry. A call with no
 * analysis reads "Unanalysed"; it is emphatically not neutral, because "we did
 * not look" and "we looked and it was fine" lead to different actions.
 *
 * @param {object} props
 * @param {string|null} props.sentiment 'Positive' | 'Neutral' | 'Negative' | null
 * @param {boolean} [props.showLabel] false renders the icon alone, keeping the
 *   word available to screen readers and as a hover title.
 */
export function SentimentBadge({ sentiment, showLabel = true, size = 'md' }) {
  const meta = SENTIMENT_META[sentimentTone(sentiment)] || SENTIMENT_META.unknown;

  return (
    <span className={`${shellClass(size)} ${meta.chip}`} title={showLabel ? undefined : meta.label}>
      {renderIcon(meta.icon)}
      {showLabel ? meta.label : <span className="sr-only">{meta.label}</span>}
    </span>
  );
}

const DIRECTIONS = {
  in: { label: 'Inbound', icon: 'fa-phone-volume' },
  out: { label: 'Outbound', icon: 'fa-phone' },
  unknown: { label: 'Unknown', icon: 'fa-circle-question' },
};

/**
 * Call direction. Always neutral in tone — a call being inbound is a fact about
 * the call, not a verdict on it, so it gets no semantic colour.
 *
 * @param {object} props
 * @param {boolean|null} props.inbound
 */
export function DirectionBadge({ inbound, size = 'md', className = '' }) {
  const flag = asBool(inbound);
  const meta = flag === null ? DIRECTIONS.unknown : flag ? DIRECTIONS.in : DIRECTIONS.out;

  return (
    <Badge tone="neutral" size={size} icon={meta.icon} className={className}>
      {meta.label}
    </Badge>
  );
}

/**
 * What happened to the call.
 *
 * The unanswered case splits on direction because the two are different events:
 * an unanswered inbound call is a customer we lost, which is bad; an unanswered
 * outbound call is a dial that did not connect, which is ordinary. Painting both
 * red made every outbound-heavy tenant's table look like an emergency.
 *
 * @param {object} props
 * @param {boolean|null} props.answered
 * @param {boolean|null} props.inbound Only consulted when `answered` is false.
 */
export function OutcomeBadge({ answered, inbound, size = 'md', className = '' }) {
  const ans = asBool(answered);

  let tone = 'neutral';
  let label = 'Unknown';
  let icon = 'fa-circle-question';

  if (ans === true) {
    tone = 'good';
    label = 'Answered';
    icon = 'fa-check';
  } else if (ans === false) {
    // An unknown direction falls through to the neutral branch: labelling a
    // call "Missed" when we cannot confirm it was inbound overstates the loss.
    if (asBool(inbound) === true) {
      tone = 'bad';
      label = 'Missed';
      icon = 'fa-phone-slash';
    } else {
      tone = 'neutral';
      label = 'No answer';
      icon = 'fa-minus';
    }
  }

  return (
    <Badge tone={tone} size={size} icon={icon} className={className}>
      {label}
    </Badge>
  );
}
